/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { FunctionComponent, useState } from "react";

import { StrapiPlanMatrixRow } from "@/types/strapi";

import { BorderRadiuses } from "@/tokens/border";
import { Colors } from "@/tokens/color";
import { Spacing } from "@/tokens/spacing";

import { Divider } from "@/ui/atoms/divider";
import { Flag } from "@/ui/atoms/flag";
import { GridColumn } from "@/ui/atoms/grid_column";
import { GridContainer } from "@/ui/atoms/grid_container";
import { Icon } from "@/ui/atoms/icon";
import { Link } from "@/ui/atoms/link";
import { Text } from "@/ui/atoms/text";
import { SmallViewportPricingCategory } from "@/ui/wrappers/plan_matrix";

import { useTypedTheme } from "@/util/hooks/theme_hooks";
import { buildStylesByBreakpoint } from "@/util/style_util";
import { convertToRem } from "@/util/ui_util";

interface PlanMatrixRowProps extends StrapiPlanMatrixRow {
    currentPlanCategory: SmallViewportPricingCategory;
    isFirstRow: boolean;
    isLastRow: boolean;
    shouldShowFullMatrix: boolean;
}

export const PricingPlansMatrixRow: FunctionComponent<PlanMatrixRowProps> = (
    props,
) => {
    /**
     * State Management
     */
    const [isHovering, setIsHovering] = useState(false);

    const toggleRowHover = () => {
        setIsHovering(!isHovering);
    };

    /**
     * Styles
     */
    const theme = useTypedTheme();

    const rowDescriptionStyles = css(
        {
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
        },
        buildStylesByBreakpoint("paddingBottom", {
            extraSmall: "0",
            medium: !props.Subtitle && !props.Link ? "0" : Spacing["spacing-4"],
        }),
        buildStylesByBreakpoint("marginBottom", {
            extraSmall: Spacing["spacing-4"],
            medium: Spacing["spacing-0"],
        }),
    );

    const planRowContainerStyles = css(
        {
            alignItems: "stretch",
            display: "flex",
            flexDirection: "row",
            height: "100%",
            justifyContent: "space-evenly",
        },
        buildStylesByBreakpoint("gap", {
            extraSmall: Spacing["spacing-3"],
            medium: Spacing["spacing-4"],
            large: Spacing["spacing-6"],
        }),
        buildStylesByBreakpoint("flexWrap", {
            extraSmall: "nowrap",
            medium: "wrap",
            large: "nowrap",
        }),
    );

    const borderRadiusStyles = () => {
        const _formattedBorderRadius = convertToRem(
            BorderRadiuses.borderMedium,
        );

        return buildStylesByBreakpoint("borderRadius", {
            extraSmall: _formattedBorderRadius,
            medium: `${props.isFirstRow ? _formattedBorderRadius : "0"} ${
                props.isFirstRow ? _formattedBorderRadius : "0"
            } ${props.isLastRow ? _formattedBorderRadius : "0"} ${
                props.isLastRow ? _formattedBorderRadius : "0"
            }`,
        });
    };

    const planColumnStyles = css(
        {
            alignItems: "center",
            background: isHovering
                ? "transparent"
                : Colors[theme.backgrounds.backgroundTertiary],
            display: "flex",
            flexBasis: "1px",
            flexGrow: "1",
            justifyContent: "center",
            minHeight: convertToRem(30),
            padding: `${Spacing["spacing-3"]} ${Spacing["spacing-4"]}`,
        },
        borderRadiusStyles(),
    );

    const borderRadiusMatrixStyles = () => {
        const _formattedBorderRadius = convertToRem(
            BorderRadiuses.borderMedium,
        );

        return buildStylesByBreakpoint("borderRadius", {
            extraSmall: _formattedBorderRadius,
            medium: `${_formattedBorderRadius} ${
                props.isFirstRow ? _formattedBorderRadius : "0"
            } ${props.isLastRow ? _formattedBorderRadius : "0"} ${_formattedBorderRadius}`,
        });
    };

    const matrixRowContainerStyles = css(
        {
            background: isHovering
                ? Colors[theme.backgrounds.backgroundSecondary]
                : "none",
            marginLeft: `-${Spacing["spacing-3"]}`,
            paddingLeft: Spacing["spacing-3"],
        },
        borderRadiusMatrixStyles(),
    );

    const flagStyles = css({
        marginLeft: Spacing["spacing-1"],
    });

    /**
     * Util
     */
    const textOrCheck = (str: string) => {
        if (str === "True") {
            return <Icon slug="Check Circled" weight="fill" />;
        }

        if (str === "False") {
            return "";
        }

        return str;
    };

    /**
     * Rendering
     */
    const renderRowTitles = () => {
        return (
            <GridColumn
                className={rowDescriptionStyles}
                columnSpan={{ extraSmall: 6, medium: 3 }}
            >
                <Text
                    fontSize="TextSmall"
                    fontWeight="medium"
                    marginBottom="PricingMatrixRowItemInfo"
                    marginTop="PricingMatrixRowItemInfo"
                    themeKey="headlinePrimary"
                >
                    {props.Title}

                    {props.Flag && (
                        <Flag className={flagStyles} tag="span">
                            {props.Flag}
                        </Flag>
                    )}
                </Text>

                {props.Subtitle && (
                    <Text
                        fontSize="LabelDefault"
                        fontWeight="normal"
                        marginBottom="PricingMatrixRowItemInfo"
                        themeKey="textPrimary"
                    >
                        {props.Subtitle}
                    </Text>
                )}

                {props.Link && (
                    <Link
                        color="lighten-70"
                        fontSize="LabelDefault"
                        href={props.Link.URL}
                    >
                        {props.Link.Text}
                    </Link>
                )}
            </GridColumn>
        );
    };

    const renderPlanRowFeatures = () => {
        return (
            <GridColumn
                columnSpan={{ extraSmall: 6, medium: 9 }}
                columnStart={{ extraSmall: 1, medium: 4 }}
            >
                <div css={planRowContainerStyles}>
                    {(props.shouldShowFullMatrix ||
                        props.currentPlanCategory === "professional") && (
                        <>
                            <Text
                                className={planColumnStyles}
                                fontSize="PlanCardDetails"
                                fontWeight="normal"
                                textAlign="center"
                            >
                                {textOrCheck(props.Free_Plan_Value)}
                            </Text>

                            <Text
                                className={planColumnStyles}
                                fontSize="PlanCardDetails"
                                fontWeight="normal"
                                textAlign="center"
                            >
                                {textOrCheck(props.Pro_Plan_Value)}
                            </Text>
                        </>
                    )}

                    {(props.shouldShowFullMatrix ||
                        props.currentPlanCategory === "enterprise") && (
                        <>
                            <Text
                                className={planColumnStyles}
                                fontSize="PlanCardDetails"
                                fontWeight="normal"
                                textAlign="center"
                            >
                                {textOrCheck(props.Team_Plan_Value)}
                            </Text>

                            <Text
                                className={planColumnStyles}
                                fontSize="PlanCardDetails"
                                fontWeight="normal"
                                textAlign="center"
                            >
                                {textOrCheck(props.Enterprise_Plan_Value)}
                            </Text>
                        </>
                    )}
                </div>
            </GridColumn>
        );
    };

    return (
        <GridContainer
            className={matrixRowContainerStyles}
            onMouseEnter={toggleRowHover}
            onMouseLeave={toggleRowHover}
        >
            {renderRowTitles()}

            {renderPlanRowFeatures()}

            {!props.isLastRow && (
                <GridColumn columnSpan="full" columnStart={1}>
                    <Divider
                        marginBottom="PricingMatrixRowItem"
                        marginTop="PricingMatrixRowItem"
                    />
                </GridColumn>
            )}
        </GridContainer>
    );
};
